import type { ProgressOut, ProgressPut } from '@dcs/contracts';
import type { ProgressTransport } from '@dcs/platform/progress';

/**
 * The progress calls of the `@dcs/api` client, as a structural type: this file never imports the
 * client, so the `/ly-thuyet` entry (which imports the sync folder through the theory route) can
 * not pull it in, and typechecking the app does not depend on how it reaches `apps/api`'s types.
 * The real `ApiClient` satisfies it.
 */
export interface ProgressApi {
  listProgress(): Promise<ProgressOut[]>;
  getProgress(licenseClass: string): Promise<ProgressOut | null>;
  putProgress(
    licenseClass: string,
    body: ProgressPut,
  ): Promise<{ status: 'saved'; progress: ProgressOut } | { status: 'conflict' }>;
}

/** `fetch` as `createApiClient({ fetch })` takes it. */
export type ApiFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** What the sync uses of the `@dcs/api` module: `import('@dcs/api')` satisfies it. */
export interface ApiModule {
  createApiClient(options?: { baseUrl?: string; fetch?: ApiFetch }): ProgressApi;
}

/**
 * `ProgressTransport` over the `@dcs/api` client. `loadModule` is `() => import('@dcs/api')`,
 * written by the caller so the client (and `hono/client`) is a lazy chunk fetched once a user is
 * signed in. `putProgress` already turns 409 `progress_conflict` into `{ status: 'conflict' }`;
 * every other failure throws an `ApiClientError` (a 401 has `status: 401`, which stops the sync).
 *
 * Same origin (`/api/*` through the Pages proxy). PUTs sent while the page is hiding go through a
 * second client whose `fetch` has `keepalive: true`, so the request can outlive the page. Every
 * request carries the signal of one `AbortController`: `abort()` (sync disposed: sign-out, unmount)
 * cancels them all, and any request after it fails at once (one transport per sign-in, see
 * `useProgressSync`). `fetchImpl` is for tests.
 */
export function createApiProgressTransport(
  loadModule: () => Promise<ApiModule>,
  fetchImpl: ApiFetch = (input, init) => fetch(input, init),
): ProgressTransport {
  const controller = new AbortController();
  const withSignal =
    (keepalive: boolean): ApiFetch =>
    (input, init) =>
      fetchImpl(input, {
        ...init,
        signal: controller.signal,
        ...(keepalive ? { keepalive: true } : {}),
      });

  let loading: Promise<{ api: ProgressApi; keepalive: ProgressApi }> | null = null;
  const load = (): Promise<{ api: ProgressApi; keepalive: ProgressApi }> => {
    const pending =
      loading ??
      loadModule().then(({ createApiClient }) => ({
        api: createApiClient({ fetch: withSignal(false) }),
        keepalive: createApiClient({ fetch: withSignal(true) }),
      }));
    loading = pending;
    // A failed load (e.g. the chunk could not be fetched offline) is tried again next time.
    pending.catch(() => {
      if (loading === pending) loading = null;
    });
    return pending;
  };
  return {
    async list() {
      return (await load()).api.listProgress();
    },
    async get(licenseClass) {
      return (await load()).api.getProgress(licenseClass);
    },
    async put(licenseClass, body, options) {
      const clients = await load();
      return (options?.keepalive ? clients.keepalive : clients.api).putProgress(licenseClass, body);
    },
    abort() {
      controller.abort();
    },
  };
}
